import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Spinner } from './Spinner';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'accent';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  loading?: boolean;
  icon?: ReactNode;
  iconPosition?: 'left' | 'right';
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  icon,
  iconPosition = 'left',
  className = '',
  disabled,
  ...props
}, ref) => {
  // Restrained product-register styling: solid brand fills (no decorative
  // gradients), rounded-lg per DESIGN.md (never a pill — full-round is for
  // circles), tactile press state. Tokens
  // adapt to light/dark automatically.
  // Meridian: --brand is amber in both modes, so secondary/outline/ghost lean on
  // --accent (Ateneo blue / sky) — raw amber text fails contrast on the light bg,
  // and focus rings need the accent for the same reason.
  // focus-visible (not focus) so the ring shows for keyboard users only, never on
  // a plain mouse click.
  const baseStyles = 'inline-flex items-center justify-center font-semibold rounded-lg transition-all duration-150 active:scale-[0.98] disabled:active:scale-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-bg disabled:opacity-50 disabled:cursor-not-allowed';

  const variantStyles = {
    primary: 'bg-brand text-brand-fg hover:bg-brand/90 focus-visible:ring-accent shadow-sm',
    secondary: 'bg-accent/10 text-accent hover:bg-accent/15 focus-visible:ring-accent',
    outline: 'border border-accent text-accent hover:bg-accent/10 focus-visible:ring-accent',
    ghost: 'text-body hover:bg-surface-raised focus-visible:ring-accent',
    danger: 'bg-danger text-white hover:bg-danger/90 focus-visible:ring-danger shadow-sm',
    accent: 'bg-accent text-accent-fg hover:bg-accent/90 focus-visible:ring-accent shadow-sm',
  };

  // Sizes are tuned for mobile touch targets; on desktop (lg+, pointer-driven)
  // the large CTA trims to a more proportionate scale so it doesn't read oversized.
  const sizeStyles = {
    sm: 'px-3 py-1.5 text-sm gap-1.5',
    md: 'px-4 py-2.5 text-base gap-2',
    lg: 'px-6 py-3.5 text-lg gap-2.5 lg:px-5 lg:py-2.5 lg:text-base',
  };

  return (
    <button
      ref={ref}
      className={`
        ${baseStyles}
        ${variantStyles[variant]}
        ${sizeStyles[size]}
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <Spinner size="sm" color="current" />
      ) : (
        <>
          {icon && iconPosition === 'left' && <span>{icon}</span>}
          {children}
          {icon && iconPosition === 'right' && <span>{icon}</span>}
        </>
      )}
    </button>
  );
});

Button.displayName = 'Button';
